<template>
  <div v-if="showCommentButtons" ref="componentRef" class="relative">
    <slot />
    <CommentButton v-if="hasMinimumHeight" :canvas-id="activeCanvas?.id ?? ''" :component-id="componentId" />
  </div>
  <slot v-else />
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef, ref, nextTick, onMounted } from 'vue'
import { useResizeObserver } from '@vueuse/core'

import useCanvas from '@canvas-builder/composables/useCanvas'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import CommentButton from './CommentButton.vue'

interface CommentableComponentProps {
  componentId: string
}

defineProps<CommentableComponentProps>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { isEditMode, isAdmin, isImpact, isFullscreen, activeCanvas } = useCanvas()

// Component ref and height tracking
const componentRef = ref<HTMLElement>()
const componentHeight = ref(0)
const hasInitialMeasurement = ref(false)

// Minimum height for showing comment button (button is 36px + 12px top margin = 48px)
const MIN_HEIGHT_FOR_COMMENT_BUTTON = 48

// Function to measure component height
const measureHeight = () => {
  if (componentRef.value) {
    componentHeight.value = componentRef.value.getBoundingClientRect().height
    hasInitialMeasurement.value = true
  }
}

// Get initial height measurement after component is mounted
onMounted(() => {
  nextTick(() => {
    measureHeight()
  })
})

// Track component height changes
useResizeObserver(componentRef, (entries) => {
  // Safety check: ensure entries array is not empty
  if (entries.length === 0) return

  // Use getBoundingClientRect for consistent height measurement that includes padding/borders
  if (componentRef.value) {
    componentHeight.value = componentRef.value.getBoundingClientRect().height
    hasInitialMeasurement.value = true
  }
})

// Check if component has minimum height for comment button
const hasMinimumHeight = computed(() => {
  // If we haven't measured yet, assume it's tall enough to avoid layout shift
  if (!hasInitialMeasurement.value) return true

  return componentHeight.value >= MIN_HEIGHT_FOR_COMMENT_BUTTON
})

// Show comment buttons in edit mode or view mode (but not in fullscreen) when collaborations are enabled
const showCommentButtons = computed(() => {
  if (!launchDarkly.value.enable_collaborations || (!isAdmin.value && !isImpact.value)) return false

  // Always show in edit mode
  if (isEditMode.value) return true

  // In view mode, hide only when fullscreen
  return !isFullscreen.value
})
</script>
